import { Platform } from "quasar"
import { addRxPlugin, createRxDatabase, RxDatabase } from "rxdb"
import { RxCollectionCreator } from "rxdb/dist/types"
import { RxDBAttachmentsPlugin } from "rxdb/plugins/attachments"
import { RxDBDevModePlugin } from "rxdb/plugins/dev-mode"
import { RxDBJsonDumpPlugin } from "rxdb/plugins/json-dump"
import { wrappedKeyCompressionStorage } from "rxdb/plugins/key-compression"
import { RxDBMigrationPlugin } from "rxdb/plugins/migration"
import { RxDBQueryBuilderPlugin } from "rxdb/plugins/query-builder"
import { getRxStorageDexie } from "rxdb/plugins/storage-dexie"
import { getRxStorageLoki } from "rxdb/plugins/storage-lokijs"
import {
  AttachmentCollection,
  attachmentCollectionCreator,
  AttachmentDataCollection,
  attachmentDataCollectionCreator,
} from "src/db/collectionDefinitions/csi/attachment"
import {
  AttributeCharCollection,
  attributeCharCollectionCreator,
  AttributeCharValueCollection,
  attributeCharValueCollectionCreator,
  AttributeNumericCollection,
  attributeNumericCollectionCreator,
  AttributeNumericValueCollection,
  attributeNumericValueCollectionCreator,
  AttributeSelectCollection,
  attributeSelectCollectionCreator,
  AttributeSelectOptionsCollection,
  attributeSelectOptionsCollectionCreator,
  AttributeSelectValueCollection,
  attributeSelectValueCollectionCreator,
  AttributeTemporalCollection,
  attributeTemporalCollectionCreator,
  AttributeTemporalValueCollection,
  attributeTemporalValueCollectionCreator,
} from "src/db/collectionDefinitions/csi/attribute"
import {
  CorrectiveWorkOnItemCollection,
  correctiveWorkOnItemCollectionCreator,
} from "src/db/collectionDefinitions/csi/correctiveWork"
import {
  CorrectiveWorkItemCommentCollection,
  correctiveWorkItemCommentCollectionCreator,
} from "src/db/collectionDefinitions/csi/correctiveWorkItemComments"
import {
  IdentifierCollection,
  identifierCollectionCreator,
} from "src/db/collectionDefinitions/csi/identifier"
import {
  IssueOnItemActionsCollection,
  issueOnItemActionsCollectionCreator,
} from "src/db/collectionDefinitions/csi/issueOnItemActions"
import {
  IssueOnItemCommentCollection,
  issueOnItemCommentCollectionCreator,
} from "src/db/collectionDefinitions/csi/issueOnItemComments"
import {
  IssuePriorityCollection,
  issuePriorityCollectionCreator,
} from "src/db/collectionDefinitions/csi/issuePriorities"
import {
  IssuesOnItemCollection,
  issuesOnItemCollectionCreator,
} from "src/db/collectionDefinitions/csi/issuesOnItem"
import { ItemCollection, itemCollectionCreator } from "src/db/collectionDefinitions/csi/item"
import {
  ItemClassCollection,
  itemClassCollectionCreator,
} from "src/db/collectionDefinitions/csi/itemClass"
import {
  ItemModelCollection,
  itemModelCollectionCreator,
} from "src/db/collectionDefinitions/csi/itemModel"
import {
  ItemWorkOrderCollection,
  itemWorkOrderCollectionCreator,
} from "src/db/collectionDefinitions/csi/itemWorkOrders"
import {
  RegularWorkOnItemCollection,
  regularWorkOnItemCollectionCreator,
} from "src/db/collectionDefinitions/csi/regularWork"
import {
  RegularWorkItemCommentCollection,
  regularWorkItemCommentCollectionCreator,
} from "src/db/collectionDefinitions/csi/regularWorkItemComments"
import {
  WorkDomainWorkspaceCollection,
  workDomainWorkspaceCollectionCreator,
} from "src/db/collectionDefinitions/csi/workDomainWorkspace"
import {
  WorkOrderCorrectiveItemCollection,
  workOrderCorrectiveItemCollectionCreator,
  WorkOrderRegularItemCollection,
  workOrderRegularItemCollectionCreator,
} from "src/db/collectionDefinitions/csi/workOrder"
import {
  WorkOrderCorrectiveItemCommentCollection,
  workOrderCorrectiveItemCommentCollectionCreator,
  WorkOrderRegularItemCommentCollection,
  workOrderRegularItemCommentCollectionCreator,
} from "src/db/collectionDefinitions/csi/workOrderComments"
import {
  WorkOrderPriorityCollection,
  workOrderPriorityCollectionCreator,
} from "src/db/collectionDefinitions/csi/workOrderPriority"
import {
  ExampleItemCollection,
  exampleItemCollectionCreator,
} from "src/db/collectionDefinitions/exampleItem"
import {
  UserAvatarCollection,
  userAvatarCollectionCreator,
  UserCollection,
  userCollectionCreator,
} from "src/db/collectionDefinitions/springboard/user"
import {
  UserRoleCollection,
  userRoleCollectionCreator,
} from "src/db/collectionDefinitions/springboard/userRole"
import {
  WorkDomainGlobalCollection,
  workDomainGlobalCollectionCreator,
} from "src/db/collectionDefinitions/springboard/workDomainGlobal"
import {
  WorkspaceCollection,
  workspaceCollectionCreator,
} from "src/db/collectionDefinitions/springboard/workspace"
import {
  UnsubmittedAttachmentCollection,
  unsubmittedAttachmentCollectionCreator,
} from "src/db/collectionDefinitions/unsubmitted/unsubmittedAttachment"
import {
  UnsubmittedWorkCollection,
  unsubmittedWorkCollectionCreator,
} from "src/db/collectionDefinitions/unsubmitted/unsubmittedWork"
import {
  UnsubmittedWorkIssueCollection,
  unsubmittedWorkIssueCollectionCreator,
} from "src/db/collectionDefinitions/unsubmitted/unsubmittedWorkIssue"

//region Load plugins

addRxPlugin(RxDBMigrationPlugin)
addRxPlugin(RxDBJsonDumpPlugin)
addRxPlugin(RxDBQueryBuilderPlugin)
addRxPlugin(RxDBAttachmentsPlugin)

if (process.env.DEV || process.env.DEBUGGING) addRxPlugin(RxDBDevModePlugin)
//endregion

//region Definitions

/**
 * Available database names
 */
export type DatabaseBaseName =
  /** Contains the data as it is at the server. */
  | "base_db"
  /** Use for testing purposes and for initiating schema hashes for import inserts.
   * Testing purposes include unit tests, re-routing of data during development,
   * and, if required, performance benchmarks in production builds. */
  | "test_db"

export type DatabaseChangesName =
  /** Contains the changes made locally. */
  "changes_db" | "test_changes_db"

export type DatabaseCommonName =
  /** Contains the common non-CSI related data */
  "common_db" | "test_common_db"

type DatabaseAllName = DatabaseBaseName | DatabaseChangesName | DatabaseCommonName

enum StorageOption {
  Dexie,
  LokiMemory,
  PremiumIndexedDB,
  PremiumSQLite,
}

export interface AppBaseDBCollections {
  attachment: AttachmentCollection
  attachment_data: AttachmentDataCollection
  attribute_char: AttributeCharCollection
  attribute_char_value: AttributeCharValueCollection
  attribute_numeric: AttributeNumericCollection
  attribute_numeric_value: AttributeNumericValueCollection
  attribute_select: AttributeSelectCollection
  attribute_select_options: AttributeSelectOptionsCollection
  attribute_select_value: AttributeSelectValueCollection
  attribute_temporal: AttributeTemporalCollection
  attribute_temporal_value: AttributeTemporalValueCollection
  corrective_work_on_item: CorrectiveWorkOnItemCollection
  corrective_work_item_comments: CorrectiveWorkItemCommentCollection
  identifier: IdentifierCollection
  issue_on_item_actions: IssueOnItemActionsCollection
  issue_on_item_comments: IssueOnItemCommentCollection
  issue_priority: IssuePriorityCollection
  issues_on_item: IssuesOnItemCollection
  item: ItemCollection
  item_class: ItemClassCollection
  item_model: ItemModelCollection
  item_work_orders: ItemWorkOrderCollection
  regular_work_on_item: RegularWorkOnItemCollection
  regular_work_item_comments: RegularWorkItemCommentCollection
  work_domain_workspace: WorkDomainWorkspaceCollection
  work_order_corrective_item: WorkOrderCorrectiveItemCollection
  work_order_regular_item: WorkOrderRegularItemCollection
  work_order_corrective_comments: WorkOrderCorrectiveItemCommentCollection
  work_order_regular_comments: WorkOrderRegularItemCommentCollection
  work_order_priority: WorkOrderPriorityCollection
}

export interface AppChangesDBCollections {
  unsubmitted_attachment: UnsubmittedAttachmentCollection
  unsubmitted_work: UnsubmittedWorkCollection
  unsubmitted_work_issue: UnsubmittedWorkIssueCollection
}

export interface AppCommonDBCollections {
  example_item: ExampleItemCollection
  user: UserCollection
  user_avatar: UserAvatarCollection
  user_role: UserRoleCollection
  work_domain_global: WorkDomainGlobalCollection
  workspace: WorkspaceCollection
}

export type AppBaseDB = RxDatabase<AppBaseDBCollections>
export type AppChangesDB = RxDatabase<AppChangesDBCollections>
export type AppCommonDB = RxDatabase<AppCommonDBCollections>

export type AppAllDBCollections =
  | AppBaseDBCollections
  | AppChangesDBCollections
  | AppCommonDBCollections

/**
 * Define all collections here.
 */
const collectionBaseCreators: Record<keyof AppBaseDBCollections, RxCollectionCreator> = {
  attachment: attachmentCollectionCreator,
  attachment_data: attachmentDataCollectionCreator,
  attribute_char: attributeCharCollectionCreator,
  attribute_char_value: attributeCharValueCollectionCreator,
  attribute_numeric: attributeNumericCollectionCreator,
  attribute_numeric_value: attributeNumericValueCollectionCreator,
  attribute_select: attributeSelectCollectionCreator,
  attribute_select_options: attributeSelectOptionsCollectionCreator,
  attribute_select_value: attributeSelectValueCollectionCreator,
  attribute_temporal: attributeTemporalCollectionCreator,
  attribute_temporal_value: attributeTemporalValueCollectionCreator,
  corrective_work_on_item: correctiveWorkOnItemCollectionCreator,
  corrective_work_item_comments: correctiveWorkItemCommentCollectionCreator,
  identifier: identifierCollectionCreator,
  issue_on_item_actions: issueOnItemActionsCollectionCreator,
  issue_on_item_comments: issueOnItemCommentCollectionCreator,
  issue_priority: issuePriorityCollectionCreator,
  issues_on_item: issuesOnItemCollectionCreator,
  item: itemCollectionCreator,
  item_class: itemClassCollectionCreator,
  item_model: itemModelCollectionCreator,
  item_work_orders: itemWorkOrderCollectionCreator,
  regular_work_on_item: regularWorkOnItemCollectionCreator,
  regular_work_item_comments: regularWorkItemCommentCollectionCreator,
  work_domain_workspace: workDomainWorkspaceCollectionCreator,
  work_order_corrective_item: workOrderCorrectiveItemCollectionCreator,
  work_order_regular_item: workOrderRegularItemCollectionCreator,
  work_order_corrective_comments: workOrderCorrectiveItemCommentCollectionCreator,
  work_order_regular_comments: workOrderRegularItemCommentCollectionCreator,
  work_order_priority: workOrderPriorityCollectionCreator,
}

const collectionChangesCreators: Record<keyof AppChangesDBCollections, RxCollectionCreator> = {
  unsubmitted_attachment: unsubmittedAttachmentCollectionCreator,
  unsubmitted_work: unsubmittedWorkCollectionCreator,
  unsubmitted_work_issue: unsubmittedWorkIssueCollectionCreator,
}

const collectionCommonCreators: Record<keyof AppCommonDBCollections, RxCollectionCreator> = {
  example_item: exampleItemCollectionCreator,
  user: userCollectionCreator,
  user_avatar: userAvatarCollectionCreator,
  user_role: userRoleCollectionCreator,
  work_domain_global: workDomainGlobalCollectionCreator,
  workspace: workspaceCollectionCreator,
}

//endregion

const databaseBaseInstances: Record<string, undefined | AppBaseDB> = {}
const databaseChangesInstances: Record<string, undefined | AppChangesDB> = {}
const databaseCommonInstances: Record<string, undefined | AppCommonDB> = {}

let lockDBCreation = false
let lockEnsuringCollections = false

function sleep(ms: number): Promise<undefined> {
  return new Promise((r) => setTimeout(r, ms))
}

//region Helpers for getDatabase

/**
 * Detects and create the RxDB storage to use in the current environment.
 */
function getStorage() {
  let chosenStorage

  // Detect storage
  Platform?.is?.capacitor
    ? (chosenStorage = StorageOption.PremiumSQLite)
    : (chosenStorage = StorageOption.PremiumIndexedDB)

  // Override the chosen storage (for testing purposes)
  if (process.env.OVERRIDE_RXDB_STORAGE) {
    for (const [key, value] of Object.entries(StorageOption)) {
      if (process.env.OVERRIDE_RXDB_STORAGE === key) {
        chosenStorage = value as StorageOption
        console.warn("Chosen storage overridden", process.env.OVERRIDE_RXDB_STORAGE)
      }
    }
  }

  switch (chosenStorage) {
    case StorageOption.Dexie:
      return wrappedKeyCompressionStorage({ storage: getRxStorageDexie() })

    case StorageOption.LokiMemory:
      // NOTE: In-memory may be in play for online sessions on a PC.
      //       Therefore, this is kept around to be available for testing.
      return wrappedKeyCompressionStorage({ storage: getRxStorageLoki() })

    case StorageOption.PremiumIndexedDB:
      // TODO: Replace this when possible
      return wrappedKeyCompressionStorage({ storage: getRxStorageDexie() })

    case StorageOption.PremiumSQLite:
      // TODO: Replace this when possible
      return wrappedKeyCompressionStorage({ storage: getRxStorageDexie() })
  }
}
//endregion

// TODO: We need to rework this section, this can definitely be simplified

/**
 * Get the instance for a database.
 *
 * Will instantiate a new instance if there is none, or the current instance is destroyed.
 * Otherwise, it will get the current instance and recreate any missing collections.
 *
 * @param databaseName The database to get
 */
export async function getDatabase(databaseName: string): Promise<AppBaseDB> {
  // Prevent multi-creation of database instances
  while (lockDBCreation) {
    await sleep(500)
  }

  let databaseInstance = databaseBaseInstances[databaseName]

  if (databaseInstance === undefined || databaseInstance.destroyed) {
    lockDBCreation = true // Acquire lock

    const storage = getStorage()

    databaseInstance = await createRxDatabase<AppBaseDBCollections>({
      name: databaseName,
      storage: storage,
      multiInstance: true,
    })
    databaseBaseInstances[databaseName] = databaseInstance

    lockDBCreation = false // Release lock
  }

  // Prevent multiple instances setting up collections
  while (lockDBCreation || lockEnsuringCollections) {
    await sleep(500)
  }

  lockEnsuringCollections = true // Acquire lock

  // Ensure all the required collections are in the database
  let collectionsMissing = false
  const missingCollectionCreators: Partial<typeof collectionBaseCreators> = {}
  const collectionNames = Object.keys(collectionBaseCreators) as Array<keyof AppBaseDBCollections>

  for (const collectionName of collectionNames) {
    if (
      !(collectionName in databaseInstance.collections) ||
      databaseInstance.collections[collectionName].destroyed
    ) {
      collectionsMissing = true
      missingCollectionCreators[collectionName] = collectionBaseCreators[collectionName]
    }
  }

  if (!lockDBCreation && collectionsMissing && missingCollectionCreators) {
    // Add missing collections
    await databaseInstance.addCollections(missingCollectionCreators)
    insertHooks(databaseInstance)
  }

  lockEnsuringCollections = false // Release lock

  return databaseInstance
}

export async function getChangesDatabase(databaseName: string): Promise<AppChangesDB> {
  // Prevent multi-creation of database instances
  while (lockDBCreation) {
    await sleep(500)
  }

  let databaseInstance = databaseChangesInstances[databaseName]

  if (databaseInstance === undefined || databaseInstance.destroyed) {
    lockDBCreation = true // Acquire lock

    const storage = getStorage()

    databaseInstance = await createRxDatabase<AppChangesDBCollections>({
      name: databaseName,
      storage: storage,
      multiInstance: true,
    })
    databaseChangesInstances[databaseName] = databaseInstance

    lockDBCreation = false // Release lock
  }

  // Prevent multiple instances setting up collections
  while (lockDBCreation || lockEnsuringCollections) {
    await sleep(500)
  }

  lockEnsuringCollections = true // Acquire lock

  // Ensure all the required collections are in the database
  let collectionsMissing = false
  const missingCollectionCreators: Partial<typeof collectionChangesCreators> = {}
  const collectionNames = Object.keys(collectionChangesCreators) as Array<
    keyof AppChangesDBCollections
  >

  for (const collectionName of collectionNames) {
    if (
      !(collectionName in databaseInstance.collections) ||
      databaseInstance.collections[collectionName].destroyed
    ) {
      collectionsMissing = true
      missingCollectionCreators[collectionName] = collectionChangesCreators[collectionName]
    }
  }

  if (!lockDBCreation && collectionsMissing && missingCollectionCreators) {
    // Add missing collections
    await databaseInstance.addCollections(missingCollectionCreators)
  }

  lockEnsuringCollections = false // Release lock

  return databaseInstance
}

export async function getCommonDatabase(databaseName: string): Promise<AppCommonDB> {
  // Prevent multi-creation of database instances
  while (lockDBCreation) {
    await sleep(500)
  }

  let databaseInstance = databaseCommonInstances[databaseName]

  if (databaseInstance === undefined || databaseInstance.destroyed) {
    lockDBCreation = true // Acquire lock

    const storage = getStorage()

    databaseInstance = await createRxDatabase<AppCommonDBCollections>({
      name: databaseName,
      storage: storage,
      multiInstance: true,
    })
    databaseCommonInstances[databaseName] = databaseInstance

    lockDBCreation = false // Release lock
  }

  // Prevent multiple instances setting up collections
  while (lockDBCreation || lockEnsuringCollections) {
    await sleep(500)
  }

  lockEnsuringCollections = true // Acquire lock

  // Ensure all the required collections are in the database
  let collectionsMissing = false
  const missingCollectionCreators: Partial<typeof collectionCommonCreators> = {}
  const collectionNames = Object.keys(collectionCommonCreators) as Array<
    keyof AppCommonDBCollections
  >

  for (const collectionName of collectionNames) {
    if (
      !(collectionName in databaseInstance.collections) ||
      databaseInstance.collections[collectionName].destroyed
    ) {
      collectionsMissing = true
      missingCollectionCreators[collectionName] = collectionCommonCreators[collectionName]
    }
  }

  if (!lockDBCreation && collectionsMissing && missingCollectionCreators) {
    // Add missing collections
    await databaseInstance.addCollections(missingCollectionCreators)
  }

  lockEnsuringCollections = false // Release lock

  return databaseInstance
}

function insertHooks(databaseInstance: AppBaseDB) {
  databaseInstance.attachment.preSave((attachmentNew, attachmentOld) => {
    // Get old and new attachment context and take only the unique values
    attachmentNew.context = [...new Set(attachmentOld.context.concat(attachmentNew.context))]
  }, true)
}

export async function getDB(workspaceUUID?: string) {
  const base_db = await formatCSIDBName("base_db", workspaceUUID)
  const test_db = await formatCSIDBName("test_db", workspaceUUID)

  return Boolean(process.env.PROD) ? getDatabase(base_db) : getDatabase(test_db)
}

export async function getChangesDB(workspaceUUID?: string) {
  const changes_db = await formatCSIDBName("changes_db", workspaceUUID)
  const test_changes_db = await formatCSIDBName("test_changes_db", workspaceUUID)

  return Boolean(process.env.PROD)
    ? getChangesDatabase(changes_db)
    : getChangesDatabase(test_changes_db)
}

export async function getCommonDB() {
  const common_db: DatabaseCommonName = "common_db"
  const test_common_db: DatabaseCommonName = "test_common_db"

  return Boolean(process.env.PROD)
    ? getCommonDatabase(common_db)
    : getCommonDatabase(test_common_db)
}

export async function formatCSIDBName(
  dbName: DatabaseAllName,
  workspaceUUID?: string
): Promise<string> {
  const { useAuthStore } = await import("src/stores/authStore")
  const authStore = useAuthStore()
  const uuid = workspaceUUID || authStore.selectedWorkspace?.id

  if (uuid) {
    const formattedUUID = uuid.replaceAll("-", "")
    return formattedUUID + "/" + dbName
  } else {
    return dbName
  }
}
